import dayjs from "dayjs";

import {
  IMeasurement,
  IMeasurementTypeValidation,
  IUpdateMeasurementRequest,
} from "../../../@types/admin/AdminMeasurement";

/**
 * The two Retool JS libs behind the measurement edit form (EN-5158), ported
 * from `moduleMeasurementList/lib/`:
 *
 * - `validateMeasurements.js` — the client-side check that stops a bad value
 *   being written to the clinical record. The ticket calls it out explicitly:
 *   do not drop it.
 * - `buildMeasurementsBody.js` — assembles the PUT payload from the form.
 *
 * They live here as plain functions so they can be tested directly; Retool
 * ran them as query steps chained by events, which is why the export reads as
 * two disconnected scripts.
 */

/**
 * The measurement types the Retool edit form rendered a field for, in its
 * order. This was a hard-coded `measurementTypes` state in the module, not
 * something the API returns — `/Retool/Measurements/Types` is the validation
 * catalogue and carries more entries than the form ever showed.
 */
export const EDITABLE_MEASUREMENT_TYPES: string[] = [
  "pulse_ox",
  "temperature",
  "diarrhoea",
  "heart_rate",
  "height",
  "weight",
  "fev1",
  "fev25_75",
  "dyspnea",
  "bp_systolic",
  "bp_diastolic",
  "hemoglobin",
  "blood_glucose",
  "continuous_blood_glucose",
  "cni_trough",
  "barthel_index",
  "rockwood_index",
  "respiratory_rate",
  "oxygen_rate",
  "fvc",
  "fev1_fvc",
  "creatinine",
  // "gfr" was in Retool's form but the API contract has no gfr column (it is
  // computed from creatinine server-side; only lab_gfr is editable) — edits
  // to it were silently dropped, so the field is not offered.
  "white_cells",
  "psa",
  "phosphate",
  "sputum_volume",
  "calculated_predicted_fvc",
  "calculated_predicted_fev1",
  "pef",
  "fev6",
  "dry_weight",
  "crp",
  "esr",
  "nebulizer",
  "potassium",
  "tacrolimus_level",
  "bkv",
  "total_cholesterol",
  "hdl_cholesterol",
  "ldl_cholesterol",
  "calcium",
  "plasma_glucose",
  "corrected_calcium",
  "cf_dna",
  "lab_gfr",
  "neutrophils",
];

/** One editable field: the column, its label, and the row's current value. */
export interface IMeasurementField {
  key: string;
  label: string;
  value: string;
}

/** The row's value for a column as an editable string. */
function readValue(measurement: IMeasurement, key: string): string {
  const value = measurement[key];
  if (value === null || value === undefined) return "";
  return String(value);
}

/**
 * The edit form's fields — every editable measurement type, seeded from the
 * row, labelled from the validation catalogue.
 *
 * Retool labelled each input with the raw column key ("bp_systolic"), because
 * its ListView bound `label="{{ item.key }}"` while the display names sat
 * unused in `getMeasurementTypes.data`. The names are used here.
 */
export function buildMeasurementFields(
  measurement: IMeasurement,
  types: IMeasurementTypeValidation[]
): IMeasurementField[] {
  const labels = new Map(
    types.map((type) => [type.measurementType, type.displayName])
  );

  return EDITABLE_MEASUREMENT_TYPES.map((key) => ({
    key,
    label: labels.get(key) ?? key,
    value: readValue(measurement, key),
  }));
}

/**
 * Client-side validation, ported from `validateMeasurements.js`.
 *
 * Rules, unchanged: the time cannot be in the future, and any value that is
 * present must sit within its type's min/max. A blank field is skipped — it
 * means "clear this column", not "zero".
 *
 * One rule added: a value that isn't a number at all. Retool's `Number("abc")`
 * produced NaN, and `NaN < min` and `NaN > max` are both false, so a typo
 * passed validation and was sent as `null` by the body builder — silently
 * clearing the column instead of failing.
 */
export function validateMeasurements(
  time: string,
  fields: IMeasurementField[],
  types: IMeasurementTypeValidation[]
): string[] {
  const errors: string[] = [];

  if (!time) {
    errors.push("Time is required");
  } else if (dayjs(time).isAfter(dayjs())) {
    errors.push("Time cannot be in the future");
  }

  const typesByKey = new Map(types.map((type) => [type.measurementType, type]));

  fields.forEach((field) => {
    const raw = field.value?.trim();
    if (!raw) return;

    const value = Number(raw);
    if (Number.isNaN(value)) {
      errors.push(`${field.label}: value must be a number`);
      return;
    }

    // Only the range check needs the catalogue; the NaN check above must not,
    // or a missing catalogue entry would let a typo clear the column as null.
    const typeInfo = typesByKey.get(field.key);
    if (!typeInfo) return;

    if (value < typeInfo.min || value > typeInfo.max) {
      errors.push(
        `${typeInfo.displayName}: value must be between ${typeInfo.min} and ${typeInfo.max}`
      );
    }
  });

  return errors;
}

/** `time` as the `YYYY-MM-DDTHH:mm` a `datetime-local` input expects. */
export function toDateTimeInputValue(
  isoDate: string | null | undefined
): string {
  if (!isoDate) return "";
  const parsed = dayjs(new Date(isoDate));
  return parsed.isValid() ? parsed.format("YYYY-MM-DDTHH:mm") : "";
}

/**
 * The `datetime-local` value as the UTC instant the API stores.
 *
 * The input renders the stored instant in the operator's local zone
 * (toDateTimeInputValue), so an untouched value must go back as the original
 * ISO string — re-parsing it would drop the seconds, and sending the local
 * wall time without an offset shifted the stored time by the UTC offset on
 * every save. A changed value is a local wall time; dayjs converts it to UTC.
 */
function toApiDateTime(
  inputValue: string,
  originalIso: string | null | undefined
): string | null {
  if (!inputValue) return null;
  if (originalIso && toDateTimeInputValue(originalIso) === inputValue) {
    return originalIso;
  }
  const parsed = dayjs(inputValue);
  return parsed.isValid() ? parsed.toISOString() : null;
}

/**
 * The PUT payload, ported from `buildMeasurementsBody.js`: the time plus every
 * editable column, with a blank field sent as `null` to clear it.
 *
 * Sending all of them (not only the changed ones) is deliberate and matches
 * Retool — `UpdateMeasurementRequestDto` takes the whole row, so an omitted
 * column would be indistinguishable from one cleared to null.
 */
export function buildMeasurementBody(
  time: string,
  fields: IMeasurementField[],
  originalTime?: string | null
): IUpdateMeasurementRequest {
  const body: IUpdateMeasurementRequest = {
    time: toApiDateTime(time, originalTime),
  };

  fields.forEach((field) => {
    const raw = field.value?.trim();
    body[field.key] = raw ? Number(raw) : null;
  });

  return body;
}

/** The values a row actually carries, for the archive confirmation. */
export function summariseMeasurementValues(
  measurement: IMeasurement,
  types: IMeasurementTypeValidation[]
): { label: string; value: string }[] {
  const labels = new Map(
    types.map((type) => [type.measurementType, type.displayName])
  );

  return (measurement.measurementTypes ?? []).map((key) => ({
    label: labels.get(key) ?? key,
    value: readValue(measurement, key) || "-",
  }));
}
